import { useEffect, useSyncExternalStore } from 'react'
import { loadPicLibrary, onPicLibrary, pickPhoto, picLibraryReady, type PicStory } from '@/lib/picMatch'

/**
 * One library photo per story, kept (Brian's pics.js lib(), Oct 29). The first
 * time a story asks, the matcher picks its photo and the story claims it; the
 * next story avoids photos already claimed, so cards on a page show different
 * photos (log #012). The pick is kept for the session by story id, so the same
 * story shows the same photo on the feed, the desktop list, the story page and
 * the Library, however often they re-render.
 *
 * Only for stories with no outlet photo: the outlet's photo always wins.
 */
const picked = new Map<string, string | null>()
const shown = new Set<string>()

const keyOf = (s: PicStory) => s.id || s.headline || ''

/** The story's library photo URL, or null (none fits, or the library is still loading). */
export function claimStoryPhoto(s: PicStory): string | null {
  if (!picLibraryReady()) return null
  const k = keyOf(s)
  if (!k) return null
  const hit = picked.get(k)
  if (hit !== undefined) return hit
  let m = null
  try { m = pickPhoto(s, shown) } catch { m = null }
  picked.set(k, m ? m.url : null)
  if (m) shown.add(m.id)
  return m ? m.url : null
}

const subscribe = (fn: () => void) => onPicLibrary(fn)

/**
 * The library photo for a story with no outlet photo. Pass null when the story
 * has its own photo (nothing is claimed then). Returns null until the library
 * arrives; the card shows its topic photo meanwhile.
 */
export function useStoryPhoto(story: PicStory | null | undefined): string | null {
  const ready = useSyncExternalStore(subscribe, picLibraryReady, picLibraryReady)
  const want = !!story
  useEffect(() => { if (want && !ready) loadPicLibrary().catch(() => {}) }, [want, ready])
  if (!story || !ready) return null
  return claimStoryPhoto(story)
}
